<script setup lang="ts">
import store from "@/store";
import { computed, onMounted, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import { useRouter } from "vue-router";

import { toast } from "@/common/utils/toast";
import { DefaultTheme } from "@/common/themes";

import { userAccountByMember, userAffiliateList } from "@/common/api/rest/user";
import {
  UserAccountType,
  UserAffiliateListbyMemberType,
} from "@/common/types/user";

const { t, tm } = useI18n();
const router = useRouter();

const account = ref<UserAccountType | null>(null);
const affiliate = ref<UserAffiliateListbyMemberType[] | null>(null);

const accountStatus = computed(() => ({
  ACTIVE: "Ativo",
  INACTIVE: "Inativo",
  BLOCKED: "Bloqueado",
  PAST_DUE: "Vencido",
}));

const accountType = computed(
  () => tm("technical.types") as Record<string, string>,
);

function goToAccount(account: any) {
  if (account.status === "INACTIVE") {
    toast({
      message: "Sua conta ainda não foi ativada.",
      type: "warning",
    });
    return;
  }
  store.commit("SET_TYPE", "ADMIN");
  store.commit("SET_ACCOUNT", account);
  store.commit("SET_MODULES", account.modules);
  router.push(`/${account.account_id}/home`);
}

function goToAffiliate(account: UserAffiliateListbyMemberType) {
  store.commit("SET_TYPE", account.type);
  router.push(`/${account.account_id}/home`);
}

function fecthAccount() {
  userAccountByMember(store.getters.user?.member_id)
    .then((response) => {
      const { success, data, message } = response;
      if (success) {
        account.value = data;
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    });
}

function fecthAffiliate() {
  userAffiliateList(store.getters.user?.member_id)
    .then((response) => {
      const { success, data, message } = response;
      if (success) {
        affiliate.value = data.list;
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    });
}

watch(
  () => store.getters.user,
  () => {
    fecthAccount();
    fecthAffiliate();
  },
);

onMounted(() => {
  fecthAccount();
  fecthAffiliate();

  store.commit("SET_TYPE", null);
});
</script>

<template>
  <default-theme menu-active="accounts" :account-status="account?.status">
    <section class="accounts-page">
      <div class="box">
        <div class="title">
          {{ t("common.labels.my_account") }}
        </div>
        <div class="content">
          <template v-if="account">
            <div class="item" @click="goToAccount(account)">
              <div class="name">
                {{ account.name || t("user.labels.uninformed") }}
              </div>
              <div class="description">
                {{ account.description }}
              </div>
              <div class="type">
                {{ t("common.labels.status") }}:
                {{ accountStatus[account.status] }}
              </div>
            </div>
          </template>
          <template v-else>
            <div class="empty">
              {{ t("plan.texts.account_empty") }}
            </div>
          </template>
        </div>
      </div>

      <div class="box">
        <div class="title">
          {{ t("plan.labels.affiliate_accounts") }}
        </div>
        <div class="content">
          <template v-if="affiliate?.length">
            <template v-for="(item, index) in affiliate" :key="index">
              <div class="item" @click="goToAffiliate(item)">
                <div class="name">
                  {{ item.account.name || t("user.labels.uninformed") }}
                </div>
                <div class="description">
                  {{ item.account.description }}
                </div>
                <div class="type">
                  {{ t("common.labels.type") }}: {{ accountType[item.type] }}
                </div>
              </div>
            </template>
          </template>
          <template v-else>
            <div class="empty">
              {{ t("plan.texts.affiliate_accounts") }}
            </div>
          </template>
        </div>
      </div>
    </section>
  </default-theme>
</template>

<style lang="scss" scoped>
.accounts-page {
  display: flex;
  flex-direction: column;
  gap: 1.54rem;

  .title {
    font-size: 1.25rem;
    font-weight: bold;
    text-transform: uppercase;
  }

  .box {
    display: flex;
    flex-direction: column;
    width: calc(100% - (1.375rem * 2));
    background: var(--white);
    border-radius: 0.5rem;
    padding: 1.375rem;
    gap: 1rem;

    .content {
      display: flex;
      flex-direction: column;
      gap: 0.5rem;

      .item {
        cursor: pointer;
        user-select: none;
        display: flex;
        flex-direction: column;
        gap: 0.5rem;
        padding-block: 0.75rem;
        padding-inline: 1rem;
        border: 1px solid var(--border);
        border-radius: 0.5rem;

        .name {
          font-size: 1.1rem;
          font-weight: bold;
          text-transform: uppercase;
        }

        .type {
          font-size: 0.9rem;
          text-transform: uppercase;
        }

        &:hover {
          background: var(--primary);
          color: var(--white);
        }
      }
    }
  }

  .group {
    display: flex;
    width: 100%;
    flex-direction: row;
    gap: 1.54rem;
  }
}
</style>
